import { useEffect, useRef, useState, type ReactNode } from "react";
import { createPortal } from "react-dom";
import { useTabActive } from "../../lib/tabActive";
import { Icon } from "./Icon";

/**
 * Stillenmiş giriş modalı — native window.prompt yerine.
 * ConfirmDialog ile aynı .cdlg-* iskeletini kullanır; tek satır / parola /
 * çok satır (textarea) girişini, zorunlu + minLength + özel validate ile
 * doğrular ve hatayı alan altında satır içi gösterir.
 * ESC + backdrop kapatır, Enter (textarea'da Ctrl/Cmd+Enter) onaylar.
 */
interface PromptDialogProps {
  open: boolean;
  title: string;
  message?: ReactNode;
  label?: string;
  placeholder?: string;
  initialValue?: string;
  inputType?: "text" | "password";
  multiline?: boolean;
  required?: boolean;
  minLength?: number;
  maxLength?: number;
  confirmLabel?: string;
  cancelLabel?: string;
  tone?: "danger" | "primary";
  icon?: string;
  busy?: boolean;
  /** Onaylarken değeri trim'le (varsayılan true). Parolada false geç. */
  trim?: boolean;
  /** Dönüş: hata mesajı (string) veya geçerliyse null. */
  validate?: (value: string) => string | null;
  onConfirm: (value: string) => void;
  onCancel: () => void;
}

export function PromptDialog({
  open,
  title,
  message,
  label,
  placeholder,
  initialValue = "",
  inputType = "text",
  multiline = false,
  required = true,
  minLength,
  maxLength,
  confirmLabel = "Onayla",
  cancelLabel = "Vazgeç",
  tone = "primary",
  icon = "edit",
  busy = false,
  trim = true,
  validate,
  onConfirm,
  onCancel,
}: PromptDialogProps) {
  const [value, setValue] = useState(initialValue);
  const [error, setError] = useState<string | null>(null);
  const [reveal, setReveal] = useState(false);
  const inputRef = useRef<HTMLInputElement | HTMLTextAreaElement | null>(null);

  // Açılışta değeri/hata/odak durumunu sıfırla, alanı odakla.
  useEffect(() => {
    if (!open) return;
    setValue(initialValue);
    setError(null);
    setReveal(false);
    const t = window.setTimeout(() => inputRef.current?.focus(), 30);
    return () => window.clearTimeout(t);
  }, [open, initialValue]);

  // ESC kapatır.
  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") onCancel();
    };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [open, onCancel]);

  const tabActive = useTabActive();
  if (!open) return null;
  // PASİF SEKMEDE DOM'A BASILMAZ. Portal `document.body`ye gider, yani sekme panelinin
  // `display:none` gizlemesi bu katmana ULAŞMAZ — gizli sekmenin paneli aktif sayfanın
  // üstünde durmaya devam ederdi. Burada `null` dönmek DOM'u düşürür, katmanı açan durum
  // (hangi kayıt, forma ne yazıldı) sayfanın React state'inde YAŞAMAYA DEVAM eder.
  if (!tabActive) return null;


  function validateValue(raw: string): string | null {
    const v = trim ? raw.trim() : raw;
    if (required && v.length === 0) return "Bu alan zorunlu.";
    if (minLength && v.length < minLength) return `En az ${minLength} karakter olmalı.`;
    if (validate) return validate(v);
    return null;
  }

  function submit() {
    const err = validateValue(value);
    if (err) {
      setError(err);
      inputRef.current?.focus();
      return;
    }
    onConfirm(trim ? value.trim() : value);
  }

  const isPassword = inputType === "password" && !multiline;

  return createPortal(
    <div className="cdlg-backdrop" role="presentation" onClick={onCancel}>
      <div
        className="cdlg"
        role="dialog"
        aria-modal="true"
        aria-label={title}
        onClick={(e) => e.stopPropagation()}
      >
        <span className={`cdlg-icon ${tone}`} aria-hidden="true">
          <Icon name={icon} size={20} />
        </span>
        <div className="cdlg-body">
          <h3 className="cdlg-title">{title}</h3>
          {message ? <div className="cdlg-msg" style={{ marginBottom: 12 }}>{message}</div> : null}
          <div className="form-field" style={{ margin: 0 }}>
            {label ? (
              <label className="lbl" htmlFor="pdlg-input">
                {label}
                {required ? <span className="req">*</span> : null}
              </label>
            ) : null}
            <div style={{ position: "relative" }}>
              {multiline ? (
                <textarea
                  id="pdlg-input"
                  ref={(el) => {
                    inputRef.current = el;
                  }}
                  className={`form-textarea${error ? " err" : ""}`}
                  value={value}
                  placeholder={placeholder}
                  maxLength={maxLength}
                  rows={3}
                  required={required}
                  aria-required={required}
                  aria-invalid={!!error}
                  aria-describedby={error ? "pdlg-err" : undefined}
                  onChange={(e) => {
                    setValue(e.target.value);
                    if (error) setError(null);
                  }}
                  onKeyDown={(e) => {
                    if (e.key === "Enter" && (e.ctrlKey || e.metaKey)) {
                      e.preventDefault();
                      submit();
                    }
                  }}
                />
              ) : (
                <input
                  id="pdlg-input"
                  ref={(el) => {
                    inputRef.current = el;
                  }}
                  className={`form-input${error ? " err" : ""}`}
                  type={isPassword && !reveal ? "password" : "text"}
                  value={value}
                  placeholder={placeholder}
                  maxLength={maxLength}
                  required={required}
                  aria-required={required}
                  aria-invalid={!!error}
                  aria-describedby={error ? "pdlg-err" : undefined}
                  autoComplete={isPassword ? "new-password" : "off"}
                  style={isPassword ? { paddingRight: 38 } : undefined}
                  onChange={(e) => {
                    setValue(e.target.value);
                    if (error) setError(null);
                  }}
                  onKeyDown={(e) => {
                    if (e.key === "Enter") {
                      e.preventDefault();
                      submit();
                    }
                  }}
                />
              )}
              {isPassword ? (
                <button
                  type="button"
                  className="input-icon-btn"
                  aria-label={reveal ? "Parolayı gizle" : "Parolayı göster"}
                  title={reveal ? "Gizle" : "Göster"}
                  onClick={() => setReveal((r) => !r)}
                  style={{ position: "absolute", right: 4, top: "50%", transform: "translateY(-50%)" }}
                >
                  <Icon name="eye" size={15} />
                </button>
              ) : null}
            </div>
            {error ? (
              <span id="pdlg-err" className="err" role="alert" style={{ marginTop: 5, display: "block" }}>
                {error}
              </span>
            ) : null}
          </div>
        </div>
        <div className="cdlg-foot">
          <button type="button" className="btn-md secondary" onClick={onCancel} disabled={busy}>
            {cancelLabel}
          </button>
          <button
            type="button"
            className={`btn-md ${tone === "danger" ? "danger" : "primary"}`}
            onClick={submit}
            disabled={busy}
          >
            {busy ? "İşleniyor…" : confirmLabel}
          </button>
        </div>
      </div>
    </div>,
    document.body
  );
}
